import type { ReactElement } from 'react'
import { useEffect, useRef, useState } from 'react'
import type { UseMutationResult } from '@tanstack/react-query'
import type { ConnectStep, SourceCheck, SourceFieldState } from '@/api/contract'
import { EntityChoice } from '~/components/EntityChoice'
import { Callout } from '~/components/ui/Card'
import { Button } from '~/components/ui/Controls'
import { Field, Modal } from '~/components/ui/Modal'
import { StatusPill } from '~/components/ui/Status'
import type { ConnectedCollection, DisconnectOutcome, SourceHistory } from '~/state/sources'
import {
  useAddConnection,
  useCheckSource,
  useDisconnectConnection,
  useDisconnectSource,
  useRenewConnection,
  useSaveSource,
} from '~/state/sources'

export const CONNECT_CSS = `
.nw-connect .nw-connect-owners { border: 0; margin: var(--nw-space-4) 0 0; padding: 0; }
.nw-connect .nw-connect-owners legend { color: var(--nw-text); font-weight: var(--nw-weight-strong); margin-bottom: var(--nw-space-2); padding: 0; }
/* Stated in full, because the page around this modal dresses its own h3s as
   uppercase section labels and a step's name is a title. */
.nw-connect h3 { color: var(--nw-text); font-size: var(--nw-text-title); font-weight: var(--nw-weight-strong); letter-spacing: 0; margin: 0 0 var(--nw-space-3); text-transform: none; }
.nw-connect p { color: var(--nw-body-ink); font-size: var(--nw-text-cell); line-height: 1.55; margin: 0 0 var(--nw-space-3); max-width: 62ch; }
.nw-connect .nw-connect-caveat { color: var(--nw-warning); }
.nw-connect .nw-connect-caveat strong { font-weight: var(--nw-weight-strong); }
.nw-connect .nw-connect-visit { font-weight: var(--nw-weight-strong); }
.nw-connect .nw-callout a, .nw-connect .nw-connect-visit a { color: var(--nw-accent); }
.nw-connect .nw-callout-value { font-weight: var(--nw-weight-medium); overflow-wrap: anywhere; }
.nw-connect .nw-field { margin-bottom: var(--nw-space-3); }
/* A PEM is read back character by character when it goes wrong, and a
   proportional font makes that impossible. */
.nw-connect textarea { font-family: var(--nw-font-mono); font-size: var(--nw-text-chip); line-height: 1.4; resize: vertical; width: 100%; }
.nw-connect .nw-field small { color: var(--nw-muted); font-size: var(--nw-text-chip); }
.nw-connect .nw-standing { display: flex; flex-direction: column; gap: var(--nw-space-2); list-style: none; margin: 0 0 var(--nw-space-3); padding: 0; }
.nw-connect .nw-standing li { align-items: center; display: flex; font-size: var(--nw-text-cell); gap: var(--nw-space-3); justify-content: space-between; }
.nw-connect .nw-refusal { color: var(--nw-critical); font-size: var(--nw-text-cell); margin: 0 0 var(--nw-space-3); }
/* Set apart from the walk above it, because it is the opposite of what the walk
   is for, and the two answers sit side by side so neither is the one the eye
   lands on first by accident. */
.nw-connect .nw-connect-disconnect { border-top: 1px solid var(--nw-hairline); margin-top: var(--nw-space-4); padding-top: var(--nw-space-4); }
.nw-connect .nw-connect-disconnect h4 { color: var(--nw-text); font-size: var(--nw-text-cell); font-weight: var(--nw-weight-strong); margin: 0 0 var(--nw-space-2); }
.nw-connect .nw-connect-actions { display: flex; flex-wrap: wrap; gap: var(--nw-space-2); }

/* The rail across the top: every step of the walk at once, so its length is
   known before the first one is read. */
.nw-connect-rail { display: flex; gap: 4px; margin: 0 0 var(--nw-space-4); overflow-x: auto; padding: 0 0 2px; scrollbar-width: none; }
.nw-connect-rail::-webkit-scrollbar { display: none; }
.nw-connect-rail button {
  align-items: center; background: transparent; border: 0; border-radius: var(--nw-radius-full);
  color: var(--nw-muted); display: flex; flex: none; font-size: var(--nw-text-chip);
  font-weight: var(--nw-weight-medium); gap: 7px; padding: 5px 10px 5px 5px;
}
.nw-connect-rail button:hover { background: var(--nw-hover); }
.nw-connect-rail button[aria-pressed='true'] { background: var(--nw-hover); color: var(--nw-text); font-weight: var(--nw-weight-strong); }
.nw-connect-rail b {
  align-items: center; background: var(--nw-hover); border-radius: 999px; color: var(--nw-muted);
  display: flex; flex: none; font-size: var(--nw-text-meta); height: 22px; justify-content: center;
  width: 22px;
}
.nw-connect-rail button[data-done='true'] b, .nw-connect-rail button[aria-pressed='true'] b {
  background: var(--nw-accent); color: var(--nw-card);
}
/* The names are what make the rail worth having, and six of them do not fit on
   a phone. The numbers still do, and the step's own heading says the rest. */
@media (max-width: 720px) {
  .nw-connect-rail span { display: none; }
  .nw-connect-rail button { padding: 5px; }
}

/* The guide on the left, what it is asked for on the right — one column once
   there is no room for two. */
.nw-connect-cols { display: grid; gap: var(--nw-space-6); grid-template-columns: minmax(0, 1fr) minmax(0, 300px); }
@media (max-width: 720px) {
  .nw-connect-cols { grid-template-columns: minmax(0, 1fr); gap: var(--nw-space-4); }
}
.nw-connect-ask { display: flex; flex-direction: column; gap: var(--nw-space-3); }
.nw-connect-ask > *:last-child { margin-bottom: 0; }

/* What the provider answered, in its own panel, because it is news rather than
   a field's help text. */
.nw-connect-verdict {
  border: var(--nw-hairline-width) solid var(--nw-hairline); border-radius: var(--nw-radius-panel);
  font-size: var(--nw-text-cell); line-height: 1.5; padding: var(--nw-space-3) var(--nw-space-4);
}
.nw-connect-verdict[data-ok='true'] { background: var(--nw-gain-wash); border-color: transparent; }
.nw-connect-verdict[data-ok='false'] { border-color: var(--nw-loss-edge); }
.nw-connect-verdict b { display: block; font-weight: var(--nw-weight-strong); }
.nw-connect-verdict ul { list-style: none; margin: var(--nw-space-2) 0 0; padding: 0; }
.nw-connect-verdict li { color: var(--nw-body-ink); font-size: var(--nw-text-chip); overflow-wrap: anywhere; }
`

/** What the flow needs of a source: a walk, and the boxes the walk ends in. */
export interface Guided {
  id: string
  label: string
  steps?: readonly ConnectStep[]
  fields: readonly SourceFieldState[]
  /** Whether the provider can be asked about these values before they are kept. */
  checkable?: boolean
}

/**
 * Which connection this walk is about, for a source that holds several.
 *
 * `undefined` on a source with no `connections` array at all is the singleton
 * write this flow always did. Once a source declares connections there is no
 * singleton left to fall back to (see `CredentialsSource.connections`), so the
 * walk is always one of the other two: a fresh login with nothing to disconnect
 * yet, or an existing one being renewed.
 */
export type ConnectionTarget = { id: number; label: string } | 'new'

export function hasGuide(source: Guided): boolean {
  return (source.steps ?? []).length > 0
}

/**
 * The walk to a source's credentials, drawn from what the source declares.
 *
 * Nothing here knows which source it is walking. A source that declares steps
 * is guided and one that does not is left exactly as it was, which is the only
 * way the next source added gets a guide by declaring one rather than by
 * somebody remembering to come back here.
 */
export function ConnectFlow({
  source,
  connection,
  onClose,
}: {
  source: Guided
  /** See `ConnectionTarget`. Omitted for a source with no `connections` array. */
  connection?: ConnectionTarget
  /** Handed what the save started, where it started a collection, for the page to say. */
  onClose: (collected?: ConnectedCollection) => void
}): ReactElement | null {
  const steps = source.steps ?? []
  const [at, setAt] = useState(0)
  const [values, setValues] = useState<Record<string, string>>({})
  const [label, setLabel] = useState(connection !== undefined && connection !== 'new' ? connection.label : '')
  // Hooks are called unconditionally — which one's result is read depends on
  // `connection` below. The dummy id costs nothing: a mutation nobody calls
  // never reaches the network.
  const saveSingleton = useSaveSource(source.id)
  const addConnection = useAddConnection(source.id)
  const renewConnection = useRenewConnection(
    source.id,
    connection !== undefined && connection !== 'new' ? connection.id : -1,
  )
  const check = useCheckSource(source.id)
  const disconnectSingleton = useDisconnectSource(source.id)
  const disconnectConnection = useDisconnectConnection(
    source.id,
    connection !== undefined && connection !== 'new' ? connection.id : -1,
  )
  const opening = useRef<HTMLHeadingElement>(null)
  const rail = useRef<HTMLDivElement>(null)
  const [owners, setOwners] = useState<Record<string, string>>({})

  const adding = connection === 'new'
  const existing = connection !== undefined && connection !== 'new' ? connection : undefined
  const renewing = existing !== undefined
  const save = adding ? addConnection : renewing ? renewConnection : saveSingleton

  /**
   * Focus lands on the new step's name rather than staying on the button that
   * asked for it. Pressing Next otherwise leaves a keyboard past the box the
   * step exists to fill, tabbing backwards to find it — which the browser walk
   * caught and no amount of counting tab stops would have.
   *
   * The rail follows, because six named steps do not fit across a phone and the
   * one being read is the one that has to be on screen.
   */
  useEffect(() => {
    opening.current?.focus()
    rail.current
      ?.querySelector<HTMLElement>('[aria-pressed="true"]')
      ?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' })
  }, [at])

  // A check that names the login's accounts opens the owners on what is filed
  // already, so a renewal shows last time's answers rather than blanks.
  const asked = check.data?.owners
  useEffect(() => {
    if (asked === undefined) return
    setOwners(filedOwners(asked))
  }, [asked])

  const step = steps[at]
  if (step === undefined) return null

  const last = at === steps.length - 1
  // Only the boxes actually typed into are sent, so walking the guide without
  // touching a box cannot wipe what is already stored. The owners go with them
  // only once they differ from what is on file, for the same reason.
  const ownersAnswer = asked === undefined ? undefined : ownersSetting(asked, owners)
  const ownersChanged =
    asked !== undefined && ownersAnswer !== ownersSetting(asked, filedOwners(asked))
  const typed = [
    ...Object.entries(values).filter(([, value]) => value !== ''),
    ...(ownersChanged && asked !== undefined ? [[asked.variable, ownersAnswer ?? ''] as [string, string]] : []),
  ]
  const field = source.fields.find((candidate) => candidate.variable === step.yields)

  const trimmedLabel = label.trim()
  const finish = (): void => {
    // The dialog closes on the way out, so what the connect started is
    // carried out with it: the sentence saying the source is being collected
    // belongs on the page they land back on, not on a dialog that is gone.
    const onSuccess = (connected: { collection?: ConnectedCollection }): void =>
      onClose(connected.collection)

    if (adding) {
      addConnection.mutate(
        { label: trimmedLabel, values: Object.fromEntries(typed) },
        { onSuccess },
      )
      return
    }
    if (existing !== undefined) {
      if (typed.length === 0 && trimmedLabel === existing.label) {
        onClose()
        return
      }
      renewConnection.mutate(
        {
          ...(typed.length > 0 ? { values: Object.fromEntries(typed) } : {}),
          ...(trimmedLabel !== existing.label ? { label: trimmedLabel } : {}),
        },
        { onSuccess },
      )
      return
    }
    if (typed.length === 0) {
      onClose()
      return
    }
    saveSingleton.mutate(Object.fromEntries(typed), { onSuccess })
  }

  const finishDisabled = save.isPending || (adding && trimmedLabel === '')
  const nothingChanged =
    typed.length === 0 && (existing === undefined || trimmedLabel === existing.label)
  const finishLabel = save.isPending
    ? 'Saving…'
    : adding
      ? 'Add connection'
      : nothingChanged
        ? 'Done'
        // Kept as the exact words the singleton form always used — a
        // renewal's own button changes only what it has to.
        : existing === undefined
          ? 'Save and connect'
          : 'Save'

  return (
    <Modal
      title={
        adding
          ? `Add a ${source.label} connection`
          : existing !== undefined
            ? `Renew ${existing.label}`
            : `Connect ${source.label}`
      }
      lede={`Step ${at + 1} of ${steps.length}`}
      labelledBy={`nw-connect-${source.id}`}
      width="wide"
      onClose={onClose}
      footer={
        <>
          <Button onClick={() => (at === 0 ? onClose() : setAt(at - 1))}>
            {at === 0 ? 'Cancel' : 'Back'}
          </Button>
          {last ? (
            <Button kind="primary" disabled={finishDisabled} onClick={finish}>
              {finishLabel}
            </Button>
          ) : (
            <Button kind="primary" onClick={() => setAt(at + 1)}>
              Next
            </Button>
          )}
        </>
      }
    >
      <div className="nw-connect">
        <div className="nw-connect-rail" role="group" aria-label="Step" ref={rail}>
          {steps.map((candidate, index) => (
            <button
              key={candidate.key}
              type="button"
              aria-pressed={index === at}
              data-done={index < at}
              onClick={() => setAt(index)}
            >
              <b aria-hidden="true">{index + 1}</b>
              <span>{candidate.title}</span>
            </button>
          ))}
        </div>

        <div className="nw-connect-cols">
          <div>
            {/* Out of the tab order and still focusable, because what it is for
                is announcing the step that just arrived, not being a stop on the
                way to the button that asked for it. */}
            <h3 ref={opening} tabIndex={-1}>
              {step.title}
            </h3>
            {step.body.map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}

            {/* Said on the step itself. A menu path we could not check against
                the provider's own pages, presented as fact, sends somebody
                hunting a screen that may not exist — and they cannot tell
                whether they misread it or we did. */}
            {step.caveat !== undefined && (
              <p className="nw-connect-caveat">
                <strong>Unverified. </strong>
                {step.caveat}
              </p>
            )}

            {step.visit !== undefined && (
              <p className="nw-connect-visit">
                <a href={step.visit.href} target="_blank" rel="noreferrer noopener">
                  {step.visit.label} ↗
                </a>
              </p>
            )}

            {/* What the step was read off, said whether or not it can be opened.
                A basis with no address is one only we can check, and naming it
                is what stops our word reading as the provider's. */}
            {step.cite !== undefined && (
              <Callout label="Read off">
                {step.cite.href === undefined ? (
                  step.cite.label
                ) : (
                  <a href={step.cite.href} target="_blank" rel="noreferrer noopener">
                    {step.cite.label} ↗
                  </a>
                )}
              </Callout>
            )}
          </div>

          <div className="nw-connect-ask">
            {/* Asked once, on the first step, rather than as a step of its own
                — a source that already declares steps for its credential does
                not need a second rail entry for a name the household chooses
                itself. */}
            {at === 0 && (adding || existing !== undefined) && (
              <Field id={`nw-connect-${source.id}-label`} label="What do you call this connection?" width="set">
                <input
                  id={`nw-connect-${source.id}-label`}
                  name="label"
                  type="text"
                  autoComplete="off"
                  placeholder="Personal, 1337 ApS, …"
                  value={label}
                  onChange={(event) => setLabel(event.target.value)}
                />
              </Field>
            )}

            {field !== undefined && (
              <Field id={`nw-connect-${source.id}-${field.variable}`} label={field.label} width="set">
                {/* A PEM private key is several lines, and an `<input>` strips
                    every newline out of what is pasted into it without saying
                    so — leaving a key that looks whole in the box and parses
                    nowhere. So a field that says it is multi-line gets a box
                    that can hold one, at the cost of the value being visible:
                    there is no such thing as a multi-line password box, and a
                    credential that cannot be pasted is worse than one that can
                    be read over a shoulder. */}
                {field.multiline === true ? (
                  <textarea
                    id={`nw-connect-${source.id}-${field.variable}`}
                    name={field.variable}
                    rows={6}
                    spellCheck={false}
                    autoComplete="off"
                    placeholder={field.set ? 'stored — paste to replace' : ''}
                    value={values[field.variable] ?? ''}
                    onChange={(event) =>
                      setValues((current) => ({ ...current, [field.variable]: event.target.value }))
                    }
                  />
                ) : (
                  <input
                    id={`nw-connect-${source.id}-${field.variable}`}
                    name={field.variable}
                    type={field.secret ? 'password' : 'text'}
                    autoComplete="off"
                    placeholder={field.set ? 'stored — type to replace' : ''}
                    value={values[field.variable] ?? ''}
                    onChange={(event) =>
                      setValues((current) => ({ ...current, [field.variable]: event.target.value }))
                    }
                  />
                )}
              </Field>
            )}

            {last && <Standing source={source} values={values} />}

            {last && source.checkable === true && (
              <>
                <Button
                  disabled={check.isPending}
                  onClick={() =>
                    check.mutate({
                      values: Object.fromEntries(typed),
                      ...(existing === undefined ? {} : { connectionId: existing.id }),
                    })
                  }
                >
                  {check.isPending ? 'Asking…' : 'Test the connection'}
                </Button>
                <Verdict verdict={check.data} failure={check.error} />
                {asked !== undefined && asked.accounts.length > 0 && (
                  <fieldset className="nw-connect-owners">
                    <legend>Whose is each account?</legend>
                    <p>
                      {source.label} does not say, and an account nobody names is left out of every figure
                      until someone does.
                    </p>
                    {asked.accounts.map((account) => (
                      <EntityChoice
                        key={account.key}
                        id={`nw-connect-${source.id}-owner-${account.key}`}
                        label={account.label}
                        placeholder="Choose…"
                        value={owners[account.key] ?? ''}
                        onChange={(entityKey) =>
                          setOwners((current) => ({ ...current, [account.key]: entityKey }))
                        }
                      />
                    ))}
                  </fieldset>
                )}
              </>
            )}

            {save.error && <p className="nw-refusal">{save.error.message}</p>}

            {/* Nothing to disconnect on a connection that does not exist yet —
                adding one is proved before it is kept, never taken apart
                before it is made. */}
            {last && existing !== undefined && (
              <Disconnect label={existing.label} disconnect={disconnectConnection} onClose={onClose} />
            )}
            {last && connection === undefined && source.fields.some((field) => field.set) && (
              <Disconnect label={source.label} disconnect={disconnectSingleton} onClose={onClose} />
            )}
          </div>
        </div>
      </div>
    </Modal>
  )
}

type Owners = NonNullable<SourceCheck['owners']>

function filedOwners(asked: Owners): Record<string, string> {
  return Object.fromEntries(
    asked.accounts.flatMap((account) =>
      account.entityKey === undefined ? [] : [[account.key, account.entityKey] as const],
    ),
  )
}

/**
 * The owners as the connection stores them, `account=entity` in the order the
 * provider listed the accounts. An account left on *Choose…* is left out, which
 * is what leaves it unnamed rather than filed under a guess.
 */
function ownersSetting(asked: Owners, owners: Readonly<Record<string, string>>): string {
  return asked.accounts
    .flatMap((account) => {
      const entityKey = owners[account.key] ?? ''
      return entityKey === '' ? [] : [`${account.key}=${entityKey}`]
    })
    .join(',')
}

/**
 * Disconnecting, and the question that comes with it.
 *
 * Two buttons rather than one and a default, because the figures this source
 * already contributed are not the credential: a broker that reported last year
 * is part of the record of what was true, and whether that record is worth
 * keeping is the household's decision. Deleting is asked for twice, in a
 * sentence that names what goes — no provider will send this history again, and
 * nothing here can put it back.
 */
function Disconnect({
  label,
  disconnect,
  onClose,
}: {
  label: string
  disconnect: UseMutationResult<DisconnectOutcome, Error, SourceHistory>
  onClose: () => void
}): ReactElement {
  const [confirming, setConfirming] = useState(false)

  const go = (history: SourceHistory): void => {
    // Closed with nothing, because nothing was connected: the page's own note
    // about a collection having started belongs to a save and not to this.
    disconnect.mutate(history, { onSuccess: () => onClose() })
  }

  return (
    <div className="nw-connect-disconnect">
      <h4>Disconnecting {label}</h4>
      <p>
        The stored credential goes either way and the nightly run stops asking. What happens to the
        figures this connection has already contributed is yours to decide.
      </p>

      {confirming ? (
        <>
          <p className="nw-refusal">
            Every balance, position and movement {label} has reported goes for good. No provider
            will send this history again and nothing here can put it back.
          </p>
          <div className="nw-connect-actions">
            <Button kind="danger" disabled={disconnect.isPending} onClick={() => go('delete')}>
              {disconnect.isPending ? 'Deleting…' : `Delete ${label}’s figures for good`}
            </Button>
            <Button disabled={disconnect.isPending} onClick={() => setConfirming(false)}>
              Keep them after all
            </Button>
          </div>
        </>
      ) : (
        <div className="nw-connect-actions">
          <Button kind="primary" disabled={disconnect.isPending} onClick={() => go('retain')}>
            {disconnect.isPending ? 'Disconnecting…' : 'Disconnect and keep the figures'}
          </Button>
          <Button kind="danger" disabled={disconnect.isPending} onClick={() => setConfirming(true)}>
            Disconnect and delete the figures
          </Button>
        </div>
      )}

      {disconnect.error && <p className="nw-refusal">{disconnect.error.message}</p>}
    </div>
  )
}

/**
 * What the provider said, or why it could not be asked.
 *
 * A refusal is drawn as plainly as an acceptance. Somebody who has just been
 * told their token works and then watches the save refuse would have no way to
 * tell which of the two answers to believe.
 */
export function Verdict({
  verdict,
  failure,
}: {
  verdict: SourceCheck | undefined
  failure: Error | null
}): ReactElement | null {
  if (failure) {
    return (
      <div className="nw-connect-verdict" data-ok="false" role="status">
        <b>It could not be asked</b>
        {failure.message}
      </div>
    )
  }
  if (verdict === undefined) return null

  return (
    <div className="nw-connect-verdict" data-ok={String(verdict.ok)} role="status">
      <b>{verdict.ok ? 'It works' : 'It does not work yet'}</b>
      {verdict.summary}
      {verdict.found !== undefined && verdict.found.length > 0 && (
        <ul>
          {verdict.found.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      )}
    </div>
  )
}

/**
 * What the walk has in hand, before anything is sent.
 *
 * Nothing is echoed back — a secret typed two steps ago is not printed to
 * reassure somebody it arrived — so the standing is all there is to say, and a
 * step that was skipped shows up here rather than as a save that quietly stores
 * half a credential.
 */
function Standing({
  source,
  values,
}: {
  source: Guided
  values: Readonly<Record<string, string>>
}): ReactElement | null {
  const wanted = (source.steps ?? [])
    .map((step) => source.fields.find((field) => field.variable === step.yields))
    .filter((field): field is SourceFieldState => field !== undefined)

  if (wanted.length === 0) return null

  return (
    <ul className="nw-standing">
      {wanted.map((field) => {
        const fresh = (values[field.variable] ?? '') !== ''
        return (
          <li key={field.variable}>
            <span>{field.label}</span>
            {fresh ? (
              <StatusPill tone="live" label="Ready to save" />
            ) : field.set ? (
              <StatusPill tone="manual" label="Already stored" />
            ) : (
              <StatusPill tone="caution" label="Nothing yet" />
            )}
          </li>
        )
      })}
    </ul>
  )
}
